import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, MapPin, Sparkles, X } from 'lucide-react';
import {
  setQuery,
  selectAISearchQuery,
  selectAISearchResults,
  selectAISearchLastSearchedAt,
  clearResults,
} from '../../slices/aiSearchSlice';
import {
  setCoordinates,
  selectEffectiveCoordinates,
  selectEffectiveCoordinatesAreReal,
} from '../../slices/locationSlice';
import useAISearch from '../../hooks/search/useAISearch';
import { useAiConsent } from '../../components/Consent/AiConsentProvider';
import { deriveUIState } from '../../components/AISearch/deriveUIState';
import AISummaryCard from '../../components/AISearch/AISummaryCard';
import EmergencyCTA from '../../components/AISearch/EmergencyCTA';
import QuickActions from '../../components/AISearch/QuickActions';
import CommunityPostCards from '../../components/AISearch/CommunityPostCards';
import HelpGuideCards from '../../components/AISearch/HelpGuideCards';
import HazardAlerts from '../../components/AISearch/HazardAlerts';
import PlacesCards from '../../components/AISearch/PlacesCards';
import VideoCarousel from '../../components/AISearch/VideoCarousel';
import AISearchSkeleton from '../../components/AISearch/AISearchSkeleton';
import AISearchError from '../../components/AISearch/AISearchError';
import AISearchEmptyState from '../../components/AISearch/AISearchEmptyState';
import CreatePostModal from '../../components/CreatePostModal/CreatePostModal';
import { AI_SEARCH_PROMPT_CHIPS as SUGGESTION_CHIPS } from '../../constants/aiSearchPrompts';

const RESULTS_CACHE_TTL_MS = 5 * 60 * 1000;

const INTENTS_NEEDING_LOCATION = new Set([
  'medical_emergency',
  'medical_routine',
  'find_service',
  'find_place',
  'lost_pet',
  'found_pet',
]);

const ShareLocationNotice = ({ onShareLocation }) => {
  const { t } = useTranslation();
  return (
    <section
      aria-label={t('share_location_notice', 'Share location to find nearby places')}
      className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-4 sm:px-5 sm:py-5"
    >
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center"
          aria-hidden="true"
        >
          <MapPin className="w-4 h-4 text-blue-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-blue-900">
            {t('share_location_heading', 'Share your location for nearby places')}
          </h3>
          <p className="mt-1 text-xs sm:text-sm text-blue-800/85">
            {t('share_location_body', "We couldn't determine where you are, so we couldn't look up vets, shelters, or other places near you for this search.")}
          </p>
          <button
            type="button"
            onClick={onShareLocation}
            className="mt-3 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors min-h-[40px] focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-blue-50"
          >
            <MapPin className="w-4 h-4" aria-hidden="true" />
            {t('share_my_location', 'Share my location')}
          </button>
        </div>
      </div>
    </section>
  );
};

const AISearchPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const reduxQuery = useSelector(selectAISearchQuery);
  const cachedResults = useSelector(selectAISearchResults);
  const cachedLastSearchedAt = useSelector(selectAISearchLastSearchedAt);
  const effectiveCoordinates = useSelector(selectEffectiveCoordinates);
  const coordinatesAreReal = useSelector(selectEffectiveCoordinatesAreReal);
  const { isAuthenticated } = useSelector((state) => state.auth);

  const coordinatesCacheKey = (() => {
    if (!coordinatesAreReal) return 'none';
    if (
      !Array.isArray(effectiveCoordinates) ||
      effectiveCoordinates.length !== 2
    ) {
      return 'none';
    }
    const [lng, lat] = effectiveCoordinates;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return 'none';
    return `${lat.toFixed(3)},${lng.toFixed(3)}`;
  })();
  const lastCoordinatesKeyRef = useRef(coordinatesCacheKey);
  const inputRef = useRef(null);
  const searchingStatusRef = useRef(null);
  const summaryHeadingRef = useRef(null);
  const errorHeadingRef = useRef(null);
  const prevSearchStateRef = useRef(null);

  const initialUrlQuery = searchParams.get('q') ?? '';
  const urlQuery = (searchParams.get('q') ?? '').trim();

  const initialCacheHitRef = useRef(null);
  if (initialCacheHitRef.current === null) {
    const trimmed = initialUrlQuery.trim();
    const cacheAgeMs =
      cachedLastSearchedAt != null ? Date.now() - cachedLastSearchedAt : Infinity;
    initialCacheHitRef.current =
      !!trimmed &&
      !!cachedResults &&
      trimmed === reduxQuery &&
      cacheAgeMs < RESULTS_CACHE_TTL_MS;
  }
  const initialCacheHit = initialCacheHitRef.current;

  const [searchState, setSearchState] = useState(() => {
    if (!initialUrlQuery.trim()) return 'idle';
    return initialCacheHit ? 'results' : 'searching';
  });
  const [inputValue, setInputValue] = useState(initialUrlQuery);
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [createPostType, setCreatePostType] = useState(null);

  const {
    executeSearch,
    results,
    loading,
    error,
  } = useAISearch();

  const { ensureAiConsent } = useAiConsent();

  const gateAiConsent = useCallback(
    () => ensureAiConsent(),
    [ensureAiConsent]
  );

  const lastSyncedUrlQueryRef = useRef(null);

  useEffect(() => {
    if (urlQuery === lastSyncedUrlQueryRef.current) return;
    lastSyncedUrlQueryRef.current = urlQuery;

    if (!urlQuery) {
      setSearchState('idle');
      setInputValue('');
      return;
    }

    setInputValue(urlQuery);

    const cacheAgeMs =
      cachedLastSearchedAt != null
        ? Date.now() - cachedLastSearchedAt
        : Infinity;
    const cacheValid =
      !!cachedResults &&
      urlQuery === reduxQuery &&
      cacheAgeMs < RESULTS_CACHE_TTL_MS &&
      coordinatesCacheKey === lastCoordinatesKeyRef.current;

    if (cacheValid) {
      setSearchState('results');
      return;
    }

    if (cachedResults || cachedLastSearchedAt != null) {
      dispatch(clearResults());
    }

    setSearchState('searching');
    (async () => {
      const consented = await gateAiConsent();
      if (!consented) {
        setSearchState('idle');
        setInputValue('');
        lastSyncedUrlQueryRef.current = '';
        setSearchParams({}, { replace: true });
        return;
      }
      executeSearch(urlQuery);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery]);

  useEffect(() => {
    if (coordinatesCacheKey === lastCoordinatesKeyRef.current) return;
    lastCoordinatesKeyRef.current = coordinatesCacheKey;

    if (cachedResults || cachedLastSearchedAt != null) {
      dispatch(clearResults());
    }

    if (urlQuery) {
      setSearchState('searching');
      (async () => {
        const consented = await gateAiConsent();
        if (!consented) {
          setSearchState('idle');
          return;
        }
        executeSearch(urlQuery);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinatesCacheKey]);

  useEffect(() => {
    if (searchState !== 'searching') return;
    if (loading) return;
    if (reduxQuery !== urlQuery) return;
    if (results || error) {
      setSearchState('results');
    }
  }, [searchState, loading, results, error, reduxQuery, urlQuery]);

  useEffect(() => {
    const prev = prevSearchStateRef.current;
    prevSearchStateRef.current = searchState;

    if (searchState === 'searching') {
      if (prev === 'idle' && searchingStatusRef.current) {
        searchingStatusRef.current.focus();
      }
      return;
    }

    if (searchState === 'results') {
      if (results && summaryHeadingRef.current) {
        summaryHeadingRef.current.focus();
      } else if (error && errorHeadingRef.current) {
        errorHeadingRef.current.focus();
      }
    }
  }, [searchState, results, error]);

  const handleSubmit = (event) => {
    event?.preventDefault?.();
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    inputRef.current?.blur();
    (async () => {
      const consented = await gateAiConsent();
      if (!consented) return;
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
      lastSyncedUrlQueryRef.current = trimmed;
      setSearchState('searching');
      setSearchParams({ q: trimmed }, { replace: true });
      dispatch(setQuery(trimmed));
      executeSearch(trimmed);
    })();
  };

  const handleRetry = () => {
    const trimmed = inputValue.trim() || reduxQuery.trim();
    if (!trimmed) return;
    (async () => {
      const consented = await gateAiConsent();
      if (!consented) return;
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
      setSearchState('searching');
      executeSearch(trimmed);
    })();
  };

  const handleChipClick = (chip) => {
    setInputValue(chip);
    inputRef.current?.focus();
  };

  const handleClearInput = () => {
    setInputValue('');
    inputRef.current?.focus();
  };

  const handleSuggestionSelect = (chip) => {
    if (!chip) return;
    setInputValue(chip);
    inputRef.current?.blur();
    (async () => {
      const consented = await gateAiConsent();
      if (!consented) return;
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
      lastSyncedUrlQueryRef.current = chip;
      setSearchState('searching');
      setSearchParams({ q: chip }, { replace: true });
      dispatch(setQuery(chip));
      executeSearch(chip);
    })();
  };

  const handleBack = () => {
    if (location.key === 'default') {
      navigate('/', { replace: true });
      return;
    }
    navigate(-1);
  };

  const handleShareLocation = useCallback(() => {
    if (!navigator.geolocation) {
      // eslint-disable-next-line no-console
      console.warn('[AISearchPage] Geolocation unavailable in this browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
        dispatch(
          setCoordinates({
            coordinates: [longitude, latitude],
            source: 'gps',
          })
        );
      },
      (geoError) => {
        // eslint-disable-next-line no-console
        console.warn(
          '[AISearchPage] getCurrentPosition failed:',
          geoError?.message || geoError
        );
      }
    );
  }, [dispatch]);

  const handleOpenCreatePost = useCallback(
    (postType) => {
      if (!isAuthenticated) {
        navigate('/login', {
          state: {
            from: `${location.pathname}${location.search}`,
            openCreatePost: postType,
          },
        });
        return;
      }
      setCreatePostType(postType);
      setShowCreatePostModal(true);
    },
    [isAuthenticated, navigate, location.pathname, location.search]
  );

  useEffect(() => {
    if (!isAuthenticated) return;
    const pendingPostType = location.state?.openCreatePost;
    if (!pendingPostType) return;
    setCreatePostType(pendingPostType);
    setShowCreatePostModal(true);
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [
    isAuthenticated,
    location.state,
    location.pathname,
    location.search,
    navigate,
  ]);

  const isInputEmpty = !inputValue.trim();

  const renderStickyHeader = () => (
    <div className="sticky top-0 z-50 bg-white border-b border-gray-100">
      <div className="max-w-5xl mx-auto flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3">
        <button
          type="button"
          onClick={handleBack}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-w-[40px] sm:min-w-[44px] min-h-[40px] sm:min-h-[44px] flex-shrink-0 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary/40"
          aria-label={t('go_back', 'Go back')}
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" aria-hidden="true" />
        </button>

        <form
          onSubmit={handleSubmit}
          role="search"
          className="flex-1 flex items-center gap-1.5 sm:gap-2 min-w-0"
        >
          <div className="flex-1 min-w-0 relative">
            <div className="flex items-center bg-gray-50 rounded-full border border-gray-200 overflow-hidden focus-within:ring-2 focus-within:ring-primary/40">
              <div className="pl-3 sm:pl-4 pr-2 flex-shrink-0">
                <Sparkles className="w-5 h-5 text-primary" aria-hidden="true" />
              </div>
              <input
                ref={inputRef}
                type="search"
                enterKeyHint="search"
                inputMode="search"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck="false"
                autoComplete="off"
                placeholder={t('ai_search_input_placeholder', 'Ask anything about animals...')}
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                className={`flex-1 min-w-0 py-2.5 ${isInputEmpty ? 'pr-3' : 'pr-1'} bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none text-sm truncate [&::-webkit-search-cancel-button]:appearance-none`}
                aria-label={t('ai_search_query_label', 'AI search query')}
              />
              {!isInputEmpty && (
                <button
                  type="button"
                  onClick={handleClearInput}
                  aria-label={t('clear_search', 'Clear search')}
                  className="flex-shrink-0 mr-2 sm:mr-2.5 inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <X className="w-4 h-4" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={isInputEmpty}
            className={`px-3 sm:px-5 py-2.5 rounded-full text-sm font-medium transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/40 ${
              isInputEmpty
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary/90'
            }`}
          >
            {t('search_label', 'Search')}
          </button>
        </form>
      </div>
    </div>
  );

  if (searchState === 'idle') {
    return (
      <div className="min-h-screen bg-white flex flex-col animate-in fade-in duration-300 overflow-x-hidden">
        <div className="px-4 py-3">
          <button
            type="button"
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary/40"
            aria-label={t('go_back', 'Go back')}
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center px-6">
          <div className="flex flex-col items-center w-full max-w-xl">
            <div className="w-16 h-16 mb-6">
              <Sparkles className="w-full h-full text-primary" aria-hidden="true" />
            </div>

            <h1 className="text-xl font-semibold text-gray-900 mb-2 text-center">
              {t('ai_local_expert_heading', 'Your Local Animal Expert')}
            </h1>
            <p className="text-gray-500 text-sm max-w-xs leading-relaxed text-center mb-8">
              {t('ai_local_expert_body', "I understand emergencies, lost pets, abuse situations, and everyday animal needs. Just describe what's happening.")}
            </p>

            <form
              onSubmit={handleSubmit}
              role="search"
              className="w-full max-w-md mb-4"
            >
              <div className="relative">
                <Sparkles
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 z-10 text-primary"
                  aria-hidden="true"
                />
                <input
                  ref={inputRef}
                  type="search"
                  enterKeyHint="search"
                  inputMode="search"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck="false"
                  autoComplete="off"
                  placeholder={t('ai_search_input_placeholder', 'Ask anything about animals...')}
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  className={`w-full pl-11 ${isInputEmpty ? 'pr-4' : 'pr-12'} py-2.5 rounded-full border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent transition-colors truncate [&::-webkit-search-cancel-button]:appearance-none`}
                  aria-label={t('ai_search_query_label', 'AI search query')}
                  autoFocus
                />
                {!isInputEmpty && (
                  <button
                    type="submit"
                    aria-label={t('search_label', 'Search')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </button>
                )}
              </div>
            </form>

            <div className="flex flex-wrap justify-center gap-2 max-w-md">
              {SUGGESTION_CHIPS.map((chip) => (
                <button
                  type="button"
                  key={chip}
                  onClick={() => handleChipClick(chip)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  {chip}
                </button>
              ))}
            </div>

            <p
              role="note"
              className="mt-8 max-w-md text-center text-[11px] leading-snug text-gray-500"
            >
              {t(
                'ai_search_disclosure',
                'Powered by Google Gemini AI \u2014 responses are automated and animal-focused, not veterinary or legal advice.'
              )}{' '}
              <Link
                to="/terms"
                className="font-semibold text-gray-600 underline hover:text-gray-800 hover:no-underline whitespace-nowrap"
              >
                {t('how_our_ai_works', 'How our AI works \u2192')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const renderResultsBody = () => {
    if (error) {
      return (
        <AISearchError
          ref={errorHeadingRef}
          error={error}
          onRetry={handleRetry}
        />
      );
    }

    if (!results) return null;

    const postCount = Array.isArray(results.communityPosts)
      ? results.communityPosts.length
      : 0;
    const placeCount = Array.isArray(results.googlePlaces)
      ? results.googlePlaces.length
      : 0;
    const helpGuideCount = Array.isArray(results.helpGuides)
      ? results.helpGuides.length
      : 0;

    const hasHazardContent =
      !!results.hazardStatus && results.hazardStatus !== 'none';

    const hasNoUsefulData =
      postCount === 0 &&
      placeCount === 0 &&
      helpGuideCount === 0 &&
      !hasHazardContent &&
      !results.hasRelevantData;

    if (results.isOffTopic) {
      return (
        <div
          role="region"
          aria-label={t('search_results', 'Search results')}
          className="max-w-md sm:max-w-2xl mx-auto"
        >
          <AISearchEmptyState
            variant="off_topic"
            summary={results.aiSummary}
            onSelectSuggestion={handleSuggestionSelect}
          />
        </div>
      );
    }

    if (hasNoUsefulData) {
      return (
        <div
          role="region"
          aria-label={t('search_results', 'Search results')}
          className="max-w-md sm:max-w-2xl mx-auto"
        >
          <AISearchEmptyState
            variant="no_results"
            summary={results.aiSummary}
            onSelectSuggestion={handleSuggestionSelect}
          />
        </div>
      );
    }

    const uiState = deriveUIState(results.intent, results.urgency);

    return (
      <div
        role="region"
        aria-label={t('search_results', 'Search results')}
        className="max-w-md sm:max-w-2xl mx-auto"
      >

        <div className="sr-only" aria-live="polite">
          {`Found ${postCount} community post${postCount === 1 ? '' : 's'} and ${placeCount} nearby place${placeCount === 1 ? '' : 's'}.`}
        </div>

        <div className="space-y-6">
          <EmergencyCTA
            uiState={uiState}
            googlePlaces={results.googlePlaces}
            isAiFallback={results.isAiFallback}
            poisonHotline={results.poisonHotline}
          />
          <HazardAlerts
            hazardStatus={results.hazardStatus}
            events={results.hazardEvents}
            onShareLocation={handleShareLocation}
          />
          <AISummaryCard
            summary={results.aiSummary}
            uiState={uiState}
            isOffTopic={results.isOffTopic}
            disclaimer={results.disclaimer}
            headingRef={summaryHeadingRef}
          />
          <QuickActions
            intent={results.intent}
            uiState={uiState}
            onShareLocation={handleShareLocation}
            onOpenCreatePost={handleOpenCreatePost}
          />
          <CommunityPostCards posts={results.communityPosts} />
          <HelpGuideCards guides={results.helpGuides} />
          {!coordinatesAreReal &&
          INTENTS_NEEDING_LOCATION.has(results.intent) ? (
            <ShareLocationNotice onShareLocation={handleShareLocation} />
          ) : (
            <PlacesCards
              places={results.googlePlaces}
              uiState={uiState}
              recommendedPlaceId={results.recommendedPlaceId}
            />
          )}
          <VideoCarousel intent={results.intent} topic={results.topic} />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white animate-in fade-in duration-300 overflow-x-hidden">
      {renderStickyHeader()}

      <div className="px-4 py-6">
        {searchState === 'searching' && (
          <>
            <div
              ref={searchingStatusRef}
              tabIndex={-1}
              role="status"
              aria-live="polite"
              className="max-w-md sm:max-w-2xl mx-auto mb-4 text-center text-sm text-gray-500 focus:outline-none"
            >
              Searching for the best results...
            </div>
            <AISearchSkeleton />
          </>
        )}

        {searchState === 'results' && renderResultsBody()}
      </div>

      <CreatePostModal
        isOpen={showCreatePostModal}
        onClose={() => {
          setShowCreatePostModal(false);
          setCreatePostType(null);
        }}
        initialType={createPostType}
      />
    </div>
  );
};

export default AISearchPage;
